import React, { useState, useEffect } from 'react';
import { Container, Navbar, NavDropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import '../App.css';

function TopBar() {
  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    setIsOpen(false); // Close mobile menu if open
  };

  const navVariants = {
    top: {
      background: 'rgba(10, 10, 10, 0)',
      backdropFilter: 'blur(0px)',
      borderBottom: '1px solid rgba(255, 255, 255, 0)',
      y: 0,
      width: '100%',
      top: 0,
      left: 0,
      padding: '20px 0',
      borderRadius: '0px'
    },
    scrolled: {
      background: 'rgba(10, 10, 10, 0.7)',
      backdropFilter: 'blur(15px)',
      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
      y: 20,
      width: '90%',
      left: '5%',
      top: 0,
      padding: '10px 20px',
      borderRadius: '50px',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
    },
    mobileOpen: {
      background: 'rgba(10, 10, 10, 0.95)',
      backdropFilter: 'blur(15px)',
      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
      y: 0,
      width: '100%',
      left: '0%',
      top: 0,
      padding: '20px 0',
      borderRadius: '0px',
      boxShadow: 'none'
    }
  };

  const mobileMenuVariants = {
    closed: { opacity: 0, height: 0, transition: { duration: 0.3 } },
    open: { opacity: 1, height: '100vh', transition: { duration: 0.3 } }
  };

  const linkVariants = {
    hover: {
      scale: 1.05,
      color: 'var(--accent-primary)',
      textShadow: '0 0 8px var(--accent-glow)'
    }
  };

  return (
    <>
      <motion.nav
        variants={navVariants}
        animate={isOpen ? 'mobileOpen' : (scrolled ? 'scrolled' : 'top')}
        transition={{ duration: 0.4, ease: 'easeInOut' }}
        style={{
          position: 'fixed',
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          border: '1px solid transparent'
        }}
      >
        <Container className="d-flex justify-content-between align-items-center">
          <motion.a
            href="#"
            style={{
              fontWeight: '800',
              fontSize: '1.5rem',
              color: 'var(--text-primary)',
              textDecoration: 'none'
            }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            Kevin<motion.span
              style={{ color: 'var(--accent-primary)', display: 'inline-block' }}
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ repeat: Infinity, duration: 2, repeatDelay: 3 }}
            >.</motion.span>
          </motion.a>

          {/* Desktop Menu */}
          <div className="d-none d-md-flex align-items-center gap-4">
            {['about_me', 'skills', 'education', 'projects'].map((item) => (
              <motion.a
                key={item}
                href={`#${item === 'about_me' ? 'about' : item}`}
                variants={linkVariants}
                whileHover="hover"
                style={{
                  color: 'var(--text-primary)',
                  fontWeight: '500',
                  fontSize: '0.95rem',
                  position: 'relative',
                  padding: '5px 0'
                }}
              >
                {t(item)}
                <motion.div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    height: '2px',
                    background: 'var(--accent-primary)',
                    scaleX: 0
                  }}
                  whileHover={{ scaleX: 1 }}
                  transition={{ duration: 0.2 }}
                />
              </motion.a>
            ))}

            <NavDropdown
              title={
                <motion.div
                  whileHover={{ scale: 1.1, color: 'var(--accent-primary)' }}
                  style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    color: 'var(--text-primary)' // Force color
                  }}
                >
                  <i className="fa-solid fa-language" style={{ fontSize: '1.4rem' }}></i>
                </motion.div>
              }
              id="language-nav-dropdown"
              align="end"
              className="custom-dropdown"
              style={{ marginLeft: '1rem' }} // Add spacing
            >
              <NavDropdown.Item onClick={() => changeLanguage('en')}>English</NavDropdown.Item>
              <NavDropdown.Item onClick={() => changeLanguage('zh-CN')}>中文</NavDropdown.Item>
            </NavDropdown>
          </div>

          {/* Mobile Menu Toggle */}
          <div className="d-md-none">
            <Navbar.Toggle
              aria-controls="basic-navbar-nav"
              onClick={() => setIsOpen(!isOpen)}
              style={{ border: 'none', boxShadow: 'none' }}
            >
              <i className={`fa-solid ${isOpen ? 'fa-xmark' : 'fa-bars'}`} style={{ color: 'var(--text-primary)', fontSize: '1.5rem' }}></i>
            </Navbar.Toggle>
          </div>
        </Container>
      </motion.nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial="closed"
            animate="open"
            exit="closed"
            variants={mobileMenuVariants}
            style={{
              position: 'fixed',
              top: '0',
              left: '0',
              width: '100%',
              height: '100vh',
              background: 'rgba(10, 10, 10, 0.98)',
              backdropFilter: 'blur(20px)',
              zIndex: 998, // Just behind the navbar
              paddingTop: '100px', // Space for navbar
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <Container className="py-4 d-flex flex-column align-items-center gap-3">
              {['about_me', 'skills', 'education', 'projects'].map((item) => (
                <motion.a
                  key={item}
                  href={`#${item === 'about_me' ? 'about' : item}`}
                  onClick={() => setIsOpen(false)}
                  style={{
                    color: 'var(--text-primary)',
                    fontSize: '1.2rem',
                    fontWeight: '600',
                    textDecoration: 'none'
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  {t(item)}
                </motion.a>
              ))}
              <div className="d-flex gap-3 mt-2">
                <span onClick={() => changeLanguage('en')} style={{ cursor: 'pointer', color: i18n.language === 'en' ? 'var(--accent-primary)' : 'var(--text-secondary)' }}>EN</span>
                <span style={{ color: 'var(--text-muted)' }}>|</span>
                <span onClick={() => changeLanguage('zh-CN')} style={{ cursor: 'pointer', color: i18n.language === 'zh-CN' ? 'var(--accent-primary)' : 'var(--text-secondary)' }}>中文</span>
              </div>
            </Container>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default TopBar;
